/**
 * Space Editor route — thin wrapper over the presentational `SpaceEditor`
 * full-screen takeover. Handles both the "new space" (`/settings/spaces/new`)
 * and "edit space" (`/settings/spaces/:id`) paths, resolving the source space
 * from the vault context. Saving persists via `saveSpace` and returns to the
 * Spaces list; a rejected save keeps the editor open and reports why in a
 * toast, except the two refusals `SpaceEditor` can point at something on screen
 * — a duplicate name (inline on the name field) and the client-attribute
 * refusal, CLIENT_ID_REQUIRED (inline against the attribute list, DV-684) —
 * which it renders itself once re-thrown below. Everything else, including an
 * all-clients-reversal refusal (DV-738), has nothing for the editor to point
 * at and surfaces only as this route's generic save toast. No
 * `VaultPageHeader`: the editor owns the whole screen.
 *
 * Client-scope changes (DV-501, DV-738) are staged on the editor's draft and
 * persisted by this same `saveSpace` call — the Clients panel performs no
 * mutations of its own, so there is exactly one write per save.
 */
import React, { useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useAttributeDeletionImpact } from "#common/graphql/Attribute";
import { SpaceEditor } from "#components/SpaceEditor";
import {
  isClientIdRequiredError,
  isDuplicateSpaceNameError,
} from "#components/SpaceEditor/errors";
import { spaceSaveErrorMessage } from "../utils";
import { useVaultData } from "../VaultProvider";

export const SpaceEditorRoute = ({ basePath }: { basePath: string }) => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const {
    spaces,
    domains,
    globalLib,
    reference,
    user,
    addGlobalAttr,
    saveSpace,
    showToast,
    hasCapability,
  } = useVaultData();
  const [saving, setSaving] = useState(false);
  const { getDeletionImpact } = useAttributeDeletionImpact();

  const isNew = !id || id === "new";
  const source = isNew ? null : (spaces.find((s) => s.id === id) ?? null);

  if (isNew && !hasCapability("can_create_doc_vault_space")) {
    return <Redirect to={`${basePath}/settings/spaces`} />;
  }

  return (
    <SpaceEditor
      source={source}
      domains={domains}
      globalLib={globalLib}
      onDeletionImpact={getDeletionImpact}
      allSpaces={spaces}
      reference={reference}
      isSteward={user.isSteward}
      showPreview={false}
      saving={saving}
      onAddGlobalAttr={(attr) => void addGlobalAttr(attr)}
      onSave={async (space, sid) => {
        setSaving(true);
        try {
          await saveSpace(space, sid);
          showToast(
            sid === null ? `Created space "${space.name}"` : `Updated space "${space.name}"`,
          );
          history.push(`${basePath}/settings/spaces`);
        } catch (error) {
          // The refusals `SpaceEditor` renders inline once re-thrown below are
          // left to it — toasting them too would put a second, redundant
          // "alert" role on screen at once.
          if (!isDuplicateSpaceNameError(error) && !isClientIdRequiredError(error)) {
            showToast(spaceSaveErrorMessage(space.name, error), "error");
          }
          throw error;
        } finally {
          setSaving(false);
        }
      }}
      onCancel={() => history.push(`${basePath}/settings/spaces`)}
      clientSearch
    />
  );
};
